import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiCheckCircle, FiArrowRight, FiSettings, FiEye, FiShield } = FiIcons;

function Innentuer() {
  const doorTypes = [
    {
      title: 'Zimmertüren',
      description: 'Klassische Drehtüren in verschiedenen Materialien und Designs.',
      materials: ['Massivholz', 'Furnier', 'Laminat', 'Lackoberflächen'],
      image: 'https://plus.unsplash.com/premium_photo-1683129719276-4e7ce97627cc?q=80&w=1548&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3'
    },
    {
      title: 'Schiebetüren',
      description: 'Platzsparende Lösungen für moderne Raumaufteilung.',
      materials: ['Glas', 'Holz', 'Metall-Kombinationen', 'Soft-Close-Systeme'],
      image: 'https://images.unsplash.com/photo-1663131073253-0e19e0eb5c55?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
    },
    {
      title: 'Glastüren',
      description: 'Transparente und lichtdurchlässige Designlösungen.',
      materials: ['Sicherheitsglas', 'Milchglas', 'Strukturglas', 'Farbglas'],
      image: 'https://plus.unsplash.com/premium_photo-1661903125153-d75fc37c96e9?q=80&w=1931&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
    },
    {
      title: 'Designtüren',
      description: 'Individuelle Türlösungen nach Ihren Wünschen.',
      materials: ['Maßanfertigung', 'Sonderformate', 'Individuelle Oberflächen', 'Exklusive Designs'],
      image: 'https://plus.unsplash.com/premium_photo-1676823547934-b2924754156d?q=80&w=774&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
    }
  ];

  const services = [
    { icon: FiSettings, title: 'Professionelle Montage', description: 'Fachgerechte Installation mit präziser Ausrichtung und Einstellung.' },
    { icon: FiEye, title: 'Maßanfertigung', description: 'Individuelle Türlösungen für besondere Anforderungen und Maße.' },
    { icon: FiShield, title: 'Qualitätsmaterialien', description: 'Hochwertige Beschläge und Materialien für lange Lebensdauer.' }
  ];

  const features = [
    'Präzise Vermessung vor Ort',
    'Große Auswahl an Designs und Materialien',
    'Professionelle Zargen-Montage',
    'Hochwertige Beschläge und Schlösser',
    'Schallschutz-Eigenschaften',
    'Individuelle Beratung und Planung'
  ];

  return (
    <div className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <motion.nav
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center space-x-2 text-sm text-gray-600 mb-8"
        >
          <Link to="/" className="hover:text-mk-teal-600">Startseite</Link>
          <span>/</span>
          <Link to="/services" className="hover:text-mk-teal-600">Dienstleistungen</Link>
          <span>/</span>
          <span className="text-gray-900">Innentürmontage</span>
        </motion.nav>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiHome} className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Innentürmontage
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Fachgerechte Montage von Innentüren aller Art. Von klassischen Zimmertüren bis zu modernen Designlösungen - wir sorgen für perfekte Passform und Funktionalität.
          </p>
        </motion.div>

        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <img
            src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1200&h=600&fit=crop"
            alt="Innentürmontage"
            className="w-full h-96 object-cover rounded-2xl shadow-2xl"
          />
        </motion.div>

        {/* Services Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Unsere Leistungen im Überblick
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Door Types */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Türarten und Materialien
          </h2>
          <div className="space-y-16">
            {doorTypes.map((door, index) => (
              <motion.div
                key={door.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center`}
              >
                <div className="flex-1">
                  <img
                    src={door.image}
                    alt={door.title}
                    className="w-full h-80 object-cover rounded-xl shadow-lg"
                  />
                </div>
                <div className="flex-1 space-y-6">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {door.title}
                  </h3>
                  <p className="text-lg text-gray-600">
                    {door.description}
                  </p>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      Verfügbare Materialien:
                    </h4>
                    <ul className="grid grid-cols-2 gap-2">
                      {door.materials.map((material, materialIndex) => (
                        <li key={materialIndex} className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-mk-red-700 rounded-full"></div>
                          <span className="text-gray-700">{material}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Features Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="bg-mk-teal-50 p-8 rounded-xl mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Was uns auszeichnet
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="flex items-center space-x-3"
              >
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-mk-teal-600" />
                <span className="text-gray-700 font-medium">{feature}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Process Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Von der Beratung bis zur Montage
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Beratung', description: 'Individuelle Beratung zu Materialien und Designs' },
              { step: '2', title: 'Aufmaß', description: 'Präzise Vermessung der Türöffnungen' },
              { step: '3', title: 'Lieferung', description: 'Termingerechte Anlieferung der Türelemente' },
              { step: '4', title: 'Montage', description: 'Fachgerechte Installation und Einstellung' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-red-700 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center bg-gradient-to-r from-mk-teal-500 to-mk-teal-600 text-white p-12 rounded-xl"
        >
          <h2 className="text-3xl font-bold mb-4">
            Neue Türen gefällig?
          </h2>
          <p className="text-xl text-mk-teal-100 mb-8 max-w-2xl mx-auto">
            Entdecken Sie unsere vielfältigen Türlösungen. Von der Beratung bis zur Montage - alles aus einer Hand!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center space-x-2 bg-white text-mk-teal-600 px-8 py-3 rounded-sm font-semibold hover:bg-gray-100 transition-colors"
            >
              <span>Beratung vereinbaren</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Services Navigation Footer */}
      <ServicesNavigationFooter />

      {/* Google Maps Section */}
      <GoogleMaps />
    </div>
  );
}

export default Innentuer;